<template>
  <div>

    <b-row>
      <b-col cols="4">
        <b-form-group label="FIND" label-class="text-truncate">
          <b-input-group>
            <b-input type="text" v-model="textFind"/>
            <template #append>
              <b-input-group-text v-if="textFind.length"
                                  class="cursor-pointer"
                                  @click="textFind = ''">
                <i class="fas fa-xmark "/>
              </b-input-group-text>
            </template>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group label="REPLACE / SET" label-class="text-truncate">
          <b-input-group>
            <b-input ref="setField"
                     type="text"
                     v-model="textReplace"
                     :class="{'border-danger': textReplace.length === 0 && currentOperation === 'set'}"/>
            <template #append>
              <b-input-group-text v-if="textReplace.length"
                                  class="cursor-pointer"
                                  @click="textReplace = ''">
                <i class="fas fa-xmark "/>
              </b-input-group-text>
            </template>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-checkbox class="mb-2" v-model="word">FULL WORDS</b-checkbox>
      </b-col>
    </b-row>

    <b-row class="mb-3">
      <b-col v-if="bulkActionRunning">
        <pulse-loader :loading="bulkActionRunning"
                      size="10px"
                      color="#00C0F4"
                      class="mx-2"/>
      </b-col>

      <b-col v-else>
        <div class="d-flex justify-content-between">
          <div v-if="currentOperation" class="d-flex align-items-center">


            <span v-if="currentOperation === 'set'">
              <span v-if="textReplace.length">
                <span>{{ currentOperation }}</span>
                <code>{{ textReplace }}</code> to
              </span>
              <code>{{ editingSelectionCount }}</code> selected assets
            </span>

            <span v-else-if="currentOperation === 'delete'">
              <span>{{ currentOperation }}</span>
              <code>{{ textFind }}</code> from <code>{{ editingSelectionCount }}</code> selected assets
            </span>

            <span v-else-if="currentOperation === 'replace'">
              <span>{{ currentOperation }}</span>
              <code>{{ textFind }}</code> with <code>{{ textReplace }}</code> in <code>{{
                editingSelectionCount
              }}</code> selected assets
          </span>
          </div>
          <div class="flex-grow-1"></div>

          <b-button :variant="currentOperationVariant"
                    size="sm"
                    v-if="currentOperation"
                    class="mr-2 text-uppercase"
                    :disabled="!canPerformTextOperation || editingSelectionIds.length === 0"
                    @click="findAndReplace">
            <span v-if="currentOperation==='delete'">
              <i class="fas fa-trash"></i>
            </span>
            <span v-else>
            {{ currentOperation }}
            </span>
          </b-button>

          <div v-if="filteredAssets.length > 0">
            <b-button size="sm"
                      :variant="editingSelectionSelectAllButtonVariant('all')"
                      @click="editingSelectionSelectAll(true)">
              Select All
            </b-button>

            <b-button size="sm"
                      :variant="editingSelectionSelectAllButtonVariant('none')"
                      @click="editingSelectionSelectAll(false)">
              Unselect All
            </b-button>
          </div>
        </div>

      </b-col>
    </b-row>

    <div style="max-height: 60vh; overflow-y: auto">
      <b-row>
        <b-col cols="6" v-for="asset in selectedAssets" v-show="isFiltered(asset)">
          <div class="cursor-pointer border rounded mr-2 mb-1 p-1 d-flex" style="gap: 5px">
            <b-checkbox v-model="editingSelection[asset[idKeyName]]">
              <div class="img-bg img-bg-1 img-square" :style="`background-image: url('${asset.thumb_url}')`"></div>
            </b-checkbox>

            <div v-if="isInPlaceEditing(asset)" class="d-flex align-items-stretch w-100 flex-column">
              <b-textarea class="font-size-sm" style="height: 80px" v-model="inPlaceEditingAssetText"></b-textarea>
              <div class="d-flex mt-2" style="gap: 5px">
                <b-button variant="primary" size="sm" @click="saveInPlaceEditor">Save</b-button>
                <b-button variant="outline" size="sm" @click="inPlaceEditingAsset = null">Cancel</b-button>
              </div>
            </div>
            <div class="d-flex" style="gap: 10px" v-else>
              <text-highlight class="font-size-sm text-gray-600" :text="asset[propKeyName] || ''" :query="textFind"
                              @highlightsCount="handleHighlightsChanged(asset, arguments[0])"/>

              <i class="fas fa-search" @click="textFind = asset[propKeyName]"/>
              <i class="fas fa-edit" @click="inPlaceEditingAsset = asset"/>
            </div>

          </div>
        </b-col>
      </b-row>
    </div>

  </div>
</template>

<script>

import GeneralMixins from "../../../../mixins/GeneralMixins";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import TextHighlight from './TextHighlight.vue';


export default {
  props: ['selectedAssets', 'idKey', 'propKeyName'],
  mixins: [GeneralMixins],
  components: {PulseLoader, TextHighlight},


  data() {
    return {
      word: true,
      textFind: "",
      textReplace: "",
      bulkActionRunning: false,
      idKeyName: this.idKey || 'id',
      editingSelection: {},
      currentQueryAssets: {},
      inPlaceEditingAsset: null,
      inPlaceEditingAssetText: null
    }
  },

  watch: {
    inPlaceEditingAsset(asset) {
      if (asset) {
        this.inPlaceEditingAssetText = asset[this.propKeyName]
      } else {
        this.inPlaceEditingAssetText = null
      }
    },

    filteredAssets(selection) {
      if (selection && (this.textReplace.length > 0 || this.textFind.length > 0)) {
        let editingSelection = {}
        selection.forEach(asset => editingSelection[asset[this.idKeyName]] = true)
        this.editingSelection = editingSelection
      }
    },

    editingSelectionIds(val) {
      if (val.length > 0 && this.currentOperation === 'set') {
        this.$refs.setField.focus()
      }
    }
  },

  computed: {

    editingSelectionIds() {
      if (this.editingSelection) {
        return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
      }
      return []
    },

    editingSelectionCount() {
      return this.editingSelectionIds.length
    },

    filteredAssets() {
      const assetIds = Object.keys(this.currentQueryAssets)
      const filteredAssetIds = assetIds.filter(id => this.currentQueryAssets[id] > 0)

      if (this.textFind.length === 0) {
        return this.selectedAssets
      } else {
        if (filteredAssetIds.length > 0) {
          return this.selectedAssets.filter(asset => filteredAssetIds.includes(asset[this.idKeyName]))
        } else {
          return []
        }
      }
    },

    isSetOperation() {
      return (this.textFind.length === 0 && this.textReplace.length > 0) ||
          (this.textFind.length === 0 && this.textReplace.length === 0 && this.editingSelectionIds.length > 0)
    },

    currentOperation() {
      if (this.textFind.length > 0 && this.textReplace.length === 0) {
        return "delete"
      } else if (this.isSetOperation) {
        return "set"
      } else if (this.textFind.length > 0 && this.textReplace.length > 0) {
        return "replace"
      } else {
        return null
      }
    },

    currentOperationVariant() {
      if (this.currentOperation === 'set') {
        return "success"
      } else if (this.currentOperation === 'delete') {
        return "danger"
      } else if (this.currentOperation === 'replace') {
        return "primary"
      } else {
        return ""
      }
    },

    queries() {
      if (this.word && this.textFind.length > 0) {
        const regex = new RegExp("\\b" + this.textFind, "g");
        return [regex]
      } else {
        return [this.textFind]
      }
    },

    uniqueAssetNames() {
      return [...new Set(this.selectedAssets.map(el => el[this.propKeyName]).filter(el => el && el.length > 0))]
    },

    canPerformTextOperation() {
      return this.textFind.length > 0 || this.textReplace.length > 0
    },

    editingSelectionAllSelected() {
      return this.editingSelectionIds.length === this.filteredAssets.length
    },

    editingSelectionNoneSelected() {
      return this.editingSelectionIds.length === 0
    },

    canResetForm() {
      return this.textFind !== "" || this.textReplace !== "" || this.editingSelectionIds.length > 0
    }
  },

  methods: {
    resetForm() {
      this.textFind = ""
      this.textReplace = ""
      this.editingSelection = {}
    },

    editingSelectionSelectAll(value) {
      let editingSelection = {}
      this.filteredAssets.forEach(asset => editingSelection[asset[this.idKeyName]] = value)
      this.editingSelection = editingSelection
    },

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          return "primary"
        } else {
          return "outline"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline"
        }
      }
    },

    isFiltered(asset) {
      return this.filteredAssets.includes(asset)
    },

    resetText() {
      this.textFind = ""
      this.textReplace = ""
    },

    saveInPlaceEditor() {
      if (!this.inPlaceEditingAsset) {
        return;
      }

      this.$http.patch(`/curators/bulk/asset_name/${this.inPlaceEditingAsset[this.idKeyName]}`, {
        prop: this.propKeyName,
        value: this.inPlaceEditingAssetText
      }).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`${count} assets were updated successfully`, "Success")

        this.selectedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset[this.idKeyName])[0]
          if (resultAsset) {
            asset[this.propKeyName] = resultAsset[this.propKeyName]
          }
        })

        this.$emit('change', this.selectedAssets)
        this.inPlaceEditingAsset = null
        this.resetText()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    isInPlaceEditing(asset) {
      return this.inPlaceEditingAsset && this.inPlaceEditingAsset[this.idKeyName] === asset[this.idKeyName]
    },

    handleHighlightsChanged(asset, highlightsCount) {
      this.$nextTick(() => {
        // all text-highlight components write to currentQueryAssets at the same time, this causes currentQueryAssets not to be properly set with data
        this.$set(this.currentQueryAssets, asset[this.idKeyName], highlightsCount);
      })
    },


    findAndReplace() {
      if (this.currentOperation === 'delete') {
        if (!confirm(`Delete ${this.textFind} from ${this.editingSelectionIds.length} assets?`)) {
          return;
        }
      }
      this.bulkActionRunning = true

      let attributes = {
        asset_ids: this.editingSelectionIds,
        text_find: this.textFind,
        text_replace: this.textReplace,
        prop: this.propKeyName
      };

      if (this.word) {
        attributes.word = "true"
      }

      this.$http.post("/curators/bulk/asset_name", attributes).then(result => {

        let assets = result.body.assets
        let count = assets.length
        this.showToast(`${count} assets were updated successfully`, "Success")

        this.selectedAssets.forEach(asset => {
          let resultAsset = assets.filter(img => img.id === asset[this.idKeyName])[0]
          if (resultAsset) {
            asset[this.propKeyName] = resultAsset[this.propKeyName]
          }
        })

        this.$emit('change', this.selectedAssets)
        this.resetText()
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    }
  }
}
</script>